import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .ve-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #e2e8f0;
    text-align: center;
  }
  .ve-inner {
    max-width: 500px;
    width: 100%;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 24px;
    padding: 48px 32px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
  }
  .ve-icon {
    font-size: 3.5rem;
    margin-bottom: 20px;
    display: block;
  }
  .ve-heading {
    font-size: 1.5rem;
    font-weight: 800;
    margin-bottom: 14px;
  }
  .ve-heading--success  { color: #4ade80; }
  .ve-heading--info     { color: #818cf8; }
  .ve-heading--error    { color: #f87171; }
  .ve-heading--default  { color: #f1f5f9; }
  .ve-text {
    font-size: 15px;
    color: #94a3b8;
    line-height: 1.6;
    margin-bottom: 32px;
  }
  .ve-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 28px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;
    font-family: inherit;
    transition: all 0.2s;
    box-shadow: 0 4px 14px rgba(99,102,241,0.3);
    cursor: pointer;
  }
  .ve-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99,102,241,0.45);
  }
  .ve-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(99,102,241,0.2);
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: ve-spin 0.8s linear infinite;
    margin: 0 auto 24px;
  }
  @keyframes ve-spin {
    to { transform: rotate(360deg); }
  }
`;

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
    const [message, setMessage] = useState('');

    const dataFetchedRef = React.useRef(false);

    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;

        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setMessage('No verification token provided.');
            return;
        }

        const verifyEmail = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/auth/verify-email?token=${token}`, {
                    withCredentials: true
                });
                if (response.data.message.includes('already')) {
                    setStatus('already_verified');
                } else {
                    setStatus('success');
                }
                setMessage(response.data.message);
            } catch (err) {
                setStatus('error');
                setMessage(err.response?.data?.error || 'Verification failed. The link may be invalid or expired.');
            }
        };

        verifyEmail();
    }, [searchParams]);

    return (
        <div className="ve-page">
            <style>{STYLES}</style>
            <div className="ve-inner">

                {status === 'verifying' && (
                    <>
                        <div className="ve-spinner" />
                        <h1 className="ve-heading ve-heading--default">Verifying your email…</h1>
                        <p className="ve-text">Please wait a moment.</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <span className="ve-icon">✅</span>
                        <h1 className="ve-heading ve-heading--success">Email Verified!</h1>
                        <p className="ve-text">{message}</p>
                        <Link to="/login" className="ve-btn">Go to Login</Link>
                    </>
                )}

                {status === 'already_verified' && (
                    <>
                        <span className="ve-icon">ℹ️</span>
                        <h1 className="ve-heading ve-heading--info">Already Verified</h1>
                        <p className="ve-text">{message}</p>
                        <Link to="/login" className="ve-btn">Go to Login</Link>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <span className="ve-icon">❌</span>
                        <h1 className="ve-heading ve-heading--error">Verification Failed</h1>
                        <p className="ve-text">{message}</p>
                        <Link to="/signup" className="ve-btn">Back to Signup</Link>
                    </>
                )}

            </div>
        </div>
    );
};

export default VerifyEmail;
